import FWU_TARGET_TYPES from '../../enums/iot/fwuTargetTypes';
import IOT_DEVICE_TARGET_NAMES from '../../enums/iot/iodDeviceTargetNames';
import fwuTargetService from '../../services/device/fwuTargetService';
import iotDeviceFWUStateService from '../../services/iotDevice/iotDeviceFWUStateService';
import iotDeviceStateService from '../../services/iotDevice/iotDeviceFWUStateService';
import {selectIotDeviceData} from '../../state/selectors/iotDevice';
import {selectDeviceAsset, selectHolderAsset} from '../../state/selectors/yapEncrypted';
import {getState} from '../../state/store';
import versionUtils from '../../utils/versionUtils';
import connectivityService from '../device/connectivityService';
import deviceTypesService from '../device/deviceTypesService';

const getFwuPackagesStatic = ({isHolder} = {}) => {
    const isUsb = connectivityService.isUsb();
    const {device, device_engine, ble_stack, holder, holder_engine} = getFwuLoadedInfoStatic({isHolder});
    const getFwuItem = (target) => {
        if (!target) return;
        if (!target.isAvailable) return;
        if (!isUsb && target.isUsbOnly) return;

        const fwInfo = target.availableFirmwares[0];

        return {target: target.fwuTarget, version: fwInfo.version, isSblFirmware: fwInfo.isSblFirmware};
    };
    const result = [device, ble_stack, device_engine, holder, holder_engine].map(getFwuItem).filter((el) => el);

    return result;
};

const getFwuLoadedInfoStatic = ({isHolder} = {}) => {
    const iotTargets = getFwuTargetsStatic({isHolder});
    const iotDevice = selectIotDeviceData(getState());
    const fwuLoadedInfo = getFwuLoadedInfo({iotTargets, iotDevice});

    return fwuLoadedInfo;
};

const getFwuTargetsStatic = ({isHolder} = {}) => {
    const iotDevice = selectIotDeviceData(getState());

    return getFwuTargets({checkIfHolderConnected: true, iotDevice, isHolder});
};

const getFwuTargets = ({isHolder, checkIfHolderConnected, iotDevice}) => {
    const {device, device_engine, ble_stack, holder, holder_engine} = iotDevice || {};
    const {isHolderConnected} = device || {};
    const dontReturnHolderTargets = checkIfHolderConnected && !isHolderConnected;

    if (isHolder === undefined) {
        if (dontReturnHolderTargets) {
            return {device, device_engine, ble_stack};
        } else {
            return {device, device_engine, ble_stack, holder, holder_engine};
        }
    }
    if (isHolder) {
        if (dontReturnHolderTargets) {
            return {};
        } else {
            return {holder, holder_engine};
        }
    }

    return {device, device_engine};
};

const getFwuLoadedInfo = ({iotTargets, iotDevice}) => {
    const {device} = iotDevice || {};
    const result = {};
    const getFwuInfoByIotTarget = (target, fwuTarget) => {
        const {isCheckInProgress, isLoaded, isAvailable} = getFwuPackageCheckInfoByIotTarget(target);
        const availableFirmwares = target?.firmware?.availableFirmwares;
        const isSblFirmware =
            iotDeviceFWUStateService.isSblFirmware(availableFirmwares) && fwuTarget !== FWU_TARGET_TYPES.BLE_STACK;
        const doesDeviceSupportSblUpdate = checkIfDeviceSupportsSblUpdate(fwuTarget);
        const isUsbOnly =
            isSblFirmware || fwuTargetService.isFwuTargetRequiresUsbConnection(fwuTarget, doesDeviceSupportSblUpdate);

        return {
            fwuTarget,
            isAvailable,
            isCheckInProgress,
            isLoaded,
            isUsbOnly,
            availableFirmwares,
            isSblFirmware,
        };
    };
    let isInfoLoadedForAllTargets = true;
    let isInfoLoadedSuccessfullyForAllTargets = true;
    let isAnyFwuAvailable = false;
    let bleFwuCount = 0;
    let usbFwuCount = 0;

    if (iotTargets) {
        Object.entries(iotTargets).forEach(([key, value]) => {
            if (value) {
                const isHolderRelatedTarget = isHolderRelatedIotTarget(key);
                const isEngineTarget = getIsEngineTarget(key);
                const omitHolderTarget = isHolderRelatedTarget && !device?.isHolderConnected;

                if (omitHolderTarget) return;

                const fwuTarget = fwuTargetService.getFwuTargetByIotTarget({[key]: true});
                const fwuInfo = getFwuInfoByIotTarget(value, fwuTarget);

                // Do not check status engines, because it comes in availableAssociatedFirmwares node for device/holder target request
                if (fwuInfo.isCheckInProgress !== false && !isEngineTarget) {
                    isInfoLoadedForAllTargets = false;
                }
                if (!fwuInfo.isLoaded || fwuInfo.isCheckInProgress) {
                    isInfoLoadedSuccessfullyForAllTargets = false;
                }
                if (fwuInfo.isAvailable) {
                    isAnyFwuAvailable = true;
                    bleFwuCount = bleFwuCount + getBleFwuCount(fwuInfo);
                    usbFwuCount = usbFwuCount + getUsbFwuCount(fwuInfo);
                }

                result[key] = fwuInfo;
            }
        });
    }

    result.isInfoLoadedForAllTargets = isInfoLoadedForAllTargets;
    result.isInfoLoadedSuccessfullyForAllTargets = isInfoLoadedSuccessfullyForAllTargets;
    result.isAnyFwuAvailable = isAnyFwuAvailable;
    result.bleFwuCount = bleFwuCount;
    result.usbFwuCount = usbFwuCount;

    return result;
};

const getBleFwuCount = ({isUsbOnly}) => (isUsbOnly ? 0 : 1);

const getUsbFwuCount = ({isUsbOnly, isSblFirmware, fwuTarget}) => {
    if (isUsbOnly) {
        if (isSblFirmware && fwuTarget !== FWU_TARGET_TYPES.BLE_STACK) {
            // if hasBootloaderUpdate === true, target will have 2 updates
            // (it will be fetched after this bootloaderUpdate is installed)
            // BUT! for BLE_STACK it is only 1 update
            return 2;
        } else {
            return 1;
        }
    } else {
        return 0;
    }
};

const isHolderRelatedIotTarget = (key) =>
    key === IOT_DEVICE_TARGET_NAMES.HOLDER || key === IOT_DEVICE_TARGET_NAMES.HOLDER_ENGINE;

const getIsEngineTarget = (key) =>
    key === IOT_DEVICE_TARGET_NAMES.DEVICE_ENGINE || key === IOT_DEVICE_TARGET_NAMES.HOLDER_ENGINE;

const getFwuInfoByFwuTargetStatic = (fwuTarget) => {
    const {device, device_engine, ble_stack, holder, holder_engine} = getFwuLoadedInfoStatic();

    switch (fwuTarget) {
        case FWU_TARGET_TYPES.CHARGER_OR_MONO:
            return device;
        case FWU_TARGET_TYPES.MONO_ENGINE:
            return device_engine;
        case FWU_TARGET_TYPES.BLE_STACK:
            return ble_stack;
        case FWU_TARGET_TYPES.HOLDER_DEVICE:
            return holder;
        case FWU_TARGET_TYPES.HOLDER_ENGINE:
            return holder_engine;
        default:
            return null;
    }
};

const getFwuPackageCheckInfo = (fwuTarget) => {
    const iotDevice = selectIotDeviceData(getState());
    const iotTarget = fwuTargetService.getIotTargetByFwuTarget(iotDevice, fwuTarget);

    return getFwuPackageCheckInfoByIotTarget(iotTarget);
};

const getFwuPackageCheckInfoByIotTarget = (iotTarget) => {
    const isCheckInProgress = iotDeviceStateService.isFirmwareCheckInProgress(iotTarget);
    const isAvailable = iotDeviceFWUStateService.isFirmwarePackageAvailable(iotTarget);
    const isLoaded = isAvailable !== undefined;

    return {isCheckInProgress, isLoaded, isAvailable};
};

const getAvailableFirmwareVersions = (isHolder) => {
    const state = getState();
    const {holder, device, device_engine, holder_engine, ble_stack} = selectIotDeviceData(state);
    const iotTarget = isHolder ? holder : device;
    const {available_version} = iotTarget?.firmware || {};
    const doesDeviceSupportSblUpdate = checkIfDeviceSupportsSblUpdate(
        isHolder ? FWU_TARGET_TYPES.HOLDER_ENGINE : FWU_TARGET_TYPES.MONO_ENGINE
    );
    const skipAssociatedFwu = !doesDeviceSupportSblUpdate;

    const engineFirmwareVersion = skipAssociatedFwu
        ? null
        : isHolder
        ? holder_engine?.firmware?.available_version
        : device_engine?.firmware?.available_version;
    const bleStackFirmwareVersion = skipAssociatedFwu || isHolder ? null : ble_stack?.firmware?.available_version;

    return {firmwareVersion: available_version, engineFirmwareVersion, bleStackFirmwareVersion};
};

const checkIfLeviaFwuAvailable = ({iotDevice, isDelta}) => {
    const deviceType = iotDevice?.device?.type;
    const holderType = iotDevice?.holder?.type;
    const isMono = deviceTypesService.isMono(deviceType);
    const isP1V41 = deviceTypesService.isP1V41(deviceType);
    const isHolderP1V41 = deviceTypesService.isP1V41(holderType);

    if ((isMono && isP1V41) || (!isMono && isHolderP1V41)) return false;

    const ENGINE_MIN_VERSION = '100.20.0';
    const HOLDER_CONTROL_MIN_VERSION = '122.3.0';
    const DELTA_CONTROL_MIN_VERSION = '141.4.1';

    const engine = isDelta ? iotDevice?.device_engine : iotDevice?.holder_engine;
    const control = isDelta ? iotDevice?.device : iotDevice?.holder;
    const controlMinVersion = isDelta ? DELTA_CONTROL_MIN_VERSION : HOLDER_CONTROL_MIN_VERSION;

    const {firmwareVersion: engineVersion, firmware: engineFirmware} = engine || {};
    const {version: controlVersion, firmware: controlFirmware} = control || {};

    const isEngineLeviaPackageAvailable = checkIfLeviaPackageAvailable(engineFirmware, ENGINE_MIN_VERSION);
    const isEngineLeviaFwuInstalled = checkIfLeviaFwuInstalled(engineVersion, ENGINE_MIN_VERSION);

    const isControlLeviaPackageAvailable = checkIfLeviaPackageAvailable(controlFirmware, controlMinVersion);
    const isControlLeviaFwuInstalled = checkIfLeviaFwuInstalled(controlVersion, controlMinVersion);

    const result =
        ((isEngineLeviaPackageAvailable && isControlLeviaPackageAvailable) ||
            (isControlLeviaFwuInstalled && isEngineLeviaPackageAvailable) ||
            (isEngineLeviaFwuInstalled && isControlLeviaPackageAvailable)) &&
        !(isControlLeviaFwuInstalled && isEngineLeviaFwuInstalled);

    return result;
};

const checkIfLeviaPackageAvailable = (firmware, minVersion) => {
    const result = firmware?.availableFirmwares?.some(({version}) =>
        versionUtils.isFirstVersionGreaterOrEqual(version, minVersion)
    );

    return result;
};
const checkIfLeviaFwuInstalled = (currentVersion, minVersion) => {
    const result = versionUtils.isFirstVersionGreaterOrEqual(currentVersion, minVersion);

    return result;
};

const checkIfDeviceSupportsSblUpdate = (fwu_target) => {
    const isUsb = connectivityService.isUsb();

    if (isUsb) return true;

    const state = getState();
    const deviceAsset = selectDeviceAsset(state);
    const holderAsset = selectHolderAsset(state);
    const SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE = 6;

    switch (fwu_target) {
        case FWU_TARGET_TYPES.CHARGER_OR_MONO:
            return deviceAsset?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE;
        case FWU_TARGET_TYPES.MONO_ENGINE:
            return (
                deviceAsset?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE &&
                deviceAsset?.associatedDevices?.engine?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE
            );
        case FWU_TARGET_TYPES.HOLDER_DEVICE:
            if (!holderAsset) return false;

            return holderAsset?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE;
        case FWU_TARGET_TYPES.HOLDER_ENGINE:
            if (!holderAsset) return false;

            return (
                holderAsset?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE &&
                holderAsset?.associatedDevices?.engine?.sblMajorVersion >= SBL_REQUIRED_VERSION_FOR_SBL_UPDATE_VIA_BLE
            );
        default:
            return false;
    }
};

export default {
    checkIfLeviaFwuAvailable,
    getAvailableFirmwareVersions,
    getFwuLoadedInfo,
    getFwuLoadedInfoStatic,
    getFwuTargets,
    getFwuPackagesStatic,
    getFwuInfoByFwuTargetStatic,
    getFwuPackageCheckInfo,
    checkIfDeviceSupportsSblUpdate,
};
